/**
 * BillingSuccessPage.jsx
 * Exibida após redirect do Stripe Checkout com sucesso.
 *
 * IMPORTANTE: Não ativa assinatura no frontend.
 * O webhook Stripe já processou (ou está processando) a ativação no backend.
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSportsLang } from '../../contexts/SportsLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

const BillingSuccessPage = () => {
  const { lang } = useSportsLang();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [dots, setDots] = useState('.');

  const T = {
    pt: {
      title: 'Pagamento recebido!',
      activating: (d) => `Sua assinatura está sendo ativada automaticamente${d}`,
      emailNote: 'A confirmação chega por e-mail em até 2 minutos.',
      whatNext: 'O que acontece agora?',
      steps: [
        'Stripe confirma o pagamento via webhook seguro',
        'Backend ativa seu plano automaticamente',
        'Você recebe e-mail de confirmação',
        'Funcionalidades liberadas em instantes',
      ],
      viewSub: 'Ver minha assinatura →',
      dashboard: 'Ir para o painel',
      needHelp: 'Precisa de ajuda?',
    },
    en: {
      title: 'Payment received!',
      activating: (d) => `Your subscription is being activated automatically${d}`,
      emailNote: 'Confirmation arrives by email within 2 minutes.',
      whatNext: 'What happens now?',
      steps: [
        'Stripe confirms payment via secure webhook',
        'Backend activates your plan automatically',
        'You receive a confirmation email',
        'Features unlocked in moments',
      ],
      viewSub: 'View my subscription →',
      dashboard: 'Go to dashboard',
      needHelp: 'Need help?',
    },
  }[lang] || {};

  // Animação de "processando" enquanto aguarda webhook
  useEffect(() => {
    const timer = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '.' : d + '.'));
    }, 600);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: '#f8fafc' }}
    >
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-md w-full p-10 text-center">
        {/* Ícone de sucesso */}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl"
          style={{ backgroundColor: '#d1fae5' }}
        >
          ✅
        </div>

        <h1 className="text-2xl font-extrabold mb-2" style={{ color: BRAND_GREEN }}>
          {T.title}
        </h1>

        <p className="text-gray-600 text-sm mb-6">
          {T.activating(dots)}
          <br />
          <span className="text-xs text-gray-400">
            {T.emailNote}
          </span>
        </p>

        {/* Steps */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-left mb-6">
          <p className="text-xs text-blue-700 font-semibold mb-1">{T.whatNext}</p>
          <ul className="text-xs text-blue-600 space-y-1">
            {(T.steps || []).map((s, i) => <li key={i}>✓ {s}</li>)}
          </ul>
        </div>

        {sessionId && (
          <p className="text-xs text-gray-400 mb-6">
            Ref: <code className="font-mono">{sessionId.slice(0, 20)}…</code>
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Link
            to="/governance/billing"
            className="w-full py-3 rounded-xl font-bold text-white text-center block"
            style={{ backgroundColor: BRAND_GREEN }}
          >
          {T.viewSub}
        </Link>
          <Link
            to="/governance"
            className="w-full py-3 rounded-xl font-bold text-center block border-2"
            style={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
          >
            {T.dashboard}
          </Link>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-6">
        {T.needHelp}{' '}
        <a href="mailto:suporte@vukz.com.br" className="underline">
          suporte@vukz.com.br
        </a>
      </p>
    </div>
  );
};

export default BillingSuccessPage;
